import { useEffect, useState, useCallback, useContext } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';

import { Company, TimeMember, User } from '@vyce/core/src/types';
import { deleteShiftWorkersRequest, getSiteWorkersRequest } from '@vyce/core/src/api/time';
import { GetSiteWorkersSearchRequestPayload } from '@vyce/core/src/api/types';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { formatTimeSortModel } from '@vyce/core/src/utils/sorting';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

interface Props {
  siteId?: string;
  selectedCompany?: Company;
  user?: User;
  /**
   * False when the caller may not read this location's squad. The search request
   * is skipped entirely rather than fired and 403'd; defaults to true so callers
   * that do their own gating keep working.
   */
  enabled?: boolean;
}

const defaultArray: TimeMember[] = [];
const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'asc' }];

export const useLocationMembersData = ({ siteId, selectedCompany, user, enabled = true }: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [loadingTableData, setLoadingTableData] = useState<boolean>(false);
  const [workers, setWorkers] = useState<TimeMember[]>(defaultArray);
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);
  const { removeUsersFromChat } = useChatRequests({
    uid: user?.uuid ?? '',
    name: `${user?.first_name} ${user?.last_name}`,
    email: user?.email,
    phone: user?.phone,
    avatarUrl: user?.photo?.url,
  });

  const {
    sortModel,
    offset,
    page,
    substring,
    total,
    setTotal,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });

  const companyId = selectedCompany?.uuid ?? '';

  const removeWorker = async (worker: TimeMember) => {
    if (!companyId || !worker.site_id || !worker.shift_id || !worker.user_id) {
      return;
    }

    try {
      await deleteShiftWorkersRequest(companyId, worker.site_id, worker.shift_id, [worker.user_id]);
      showNotification({ message: 'Member has been removed', options: { variant: 'success' } });
      setCloseGridAction(true);
      getMembers();
      removeUsersFromChat({
        companyId,
        entityId: worker.site_id,
        userIds: [worker.user_id],
      });
    } catch (e) {
      handleServerError(e);
    }
  };

  const getMembers = useCallback(async () => {
    if (!enabled || !companyId || !siteId) {
      return setWorkers(defaultArray);
    }

    setLoadingTableData(true);
    try {
      const payload: GetSiteWorkersSearchRequestPayload = {
        offset,
        limit: GRID_PAGE_SIZE,
        substring,
        order_by: formatTimeSortModel<TimeMember>(sortModel),
        site_id: siteId,
      };
      const res = await getSiteWorkersRequest({ companyId, payload });
      setTotal(res.data.count);
      setWorkers(res.data.items);
      setLoadingTableData(false);
    } catch (e) {
      setLoadingTableData(false);
      handleServerError(e);
    }
  }, [offset, sortModel, substring, companyId, siteId, enabled]);

  useEffect(() => {
    getMembers();
  }, [getMembers]);

  return {
    total,
    workers,
    page,
    substring,
    sortModel,
    loadingTableData,
    closeGridAction,
    handlePageChange,
    handleSearchChange,
    getMembers,
    handleSortModelChange,
    removeWorker,
  };
};
